import { Connected, ServerOnline, DraftSyncSuccess, DraftSyncConflict, UserLoggedOut } from "event-definitions"
import {
    getAllPendingDrafts,
    clearLocalDraft,
    clearLocalDraftIfNotNewer,
    clearAllDrafts,
    getLocalDraft,
    markConflict,
    updateLocalDraftMetadata,
    currentUserPrefix,
} from "lib/@offline/local-draft-store"
import { withTimeout } from "lib/@offline/with-timeout"
import { restoreWriteOrderSnapshot } from "lib/graphql/write-order-link"

const MAX_RETRIES = 3
const RETRY_BACKOFF_MS = 5000
const SYNC_TIMEOUT_MS = 15000
const SYNC_COOLDOWN_MS = 5 * 60 * 1000 // 5 minutes after exhausted retries

let syncing = false
const syncingKeys = new Set()

/**
 * Registry of save functions keyed by draftKey.
 * Each editor instance registers its own save closure so the sync manager
 * replays each draft through the correct schedule-specific handler.
 */
const saveFunctions = {}

/**
 * Fallback save functions keyed by editorType (e.g. "regime-tailoring").
 * Used when no key-specific handler is registered (editor not mounted after reload/navigation).
 * These receive (data, saveContext) and must be able to reconstruct the save from the stored context.
 */
const fallbackSaveFunctions = {}
const draftRecoveryFunctions = {}

/**
 * Register a save function for a specific draft key.
 * @param {string} draftKey - The unique draft key (e.g. "tailoring:{treeId}:{scheduleId}")
 * @param {function} saveFn - Async function that takes draft data and saves to server
 */
export function registerSaveFunction(draftKey, saveFn) {
    saveFunctions[draftKey] = saveFn
}

/**
 * Unregister a save function for a specific draft key.
 * @param {string} draftKey
 */
export function unregisterSaveFunction(draftKey) {
    delete saveFunctions[draftKey]
}

/**
 * Register a fallback save function for an editor type.
 * Called at app startup (in runtime files) to ensure drafts can sync even when no editor is mounted.
 * @param {string} editorType - e.g. "regime-tailoring", "custom-schedule"
 * @param {function} saveFn - Async function that takes (data, saveContext) and saves to server
 */
export function registerFallbackSaveFunction(editorType, saveFn) {
    fallbackSaveFunctions[editorType] = saveFn
}

/**
 * Register a recovery function for an editor type.
 * Recovery functions can compare queued drafts with current server state and
 * refresh version context after conflicts/timeouts.
 * @param {string} editorType
 * @param {function} recoveryFn
 */
export function registerDraftRecoveryFunction(editorType, recoveryFn) {
    draftRecoveryFunctions[editorType] = recoveryFn
}

/**
 * Attempt to sync all pending drafts to the server.
 * Called when connectivity is restored.
 */
export async function syncPendingDrafts() {
    if (syncing) return
    syncing = true

    try {
        const drafts = await getAllPendingDrafts()
        for (const draft of drafts) {
            if (syncingKeys.has(draft.key)) continue
            // Skip drafts in cooldown after exhausted retries
            if (draft.syncFailedAt && Date.now() - draft.syncFailedAt < SYNC_COOLDOWN_MS) continue

            syncingKeys.add(draft.key)
            try {
                await syncDraftWithRetry(draft.key)
            } finally {
                syncingKeys.delete(draft.key)
            }
        }
    } finally {
        syncing = false
    }
}

export async function syncDraftNow(key) {
    if (!key || syncingKeys.has(key)) return { status: "in-progress" }
    syncingKeys.add(key)
    try {
        return await syncDraftWithRetry(key)
    } finally {
        syncingKeys.delete(key)
    }
}

async function syncDraftWithRetry(key) {
    for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
        // Re-read the draft before each attempt (last-write-wins)
        const draft = await getLocalDraft(key)
        if (!draft) return { status: "noop" } // Already cleared
        if (draft.data == null) return { status: "noop" } // Corrupt or empty draft
        const saveFn = resolveSaveFunction(draft)
        if (!saveFn) {
            console.warn("Draft sync skipped because no save function could be resolved", {
                key,
                editorType: draft.editorType,
            })
            return { status: "no-handler" }
        }
        restoreWriteOrderSnapshot(draft?.saveContext?.writeOrderKey, {
            instance: draft?.saveContext?.writeOrderInstance,
            seq: draft?.saveContext?.writeOrderSeq,
        })
        const replayAgeMs = Date.now() - (draft.updatedAt || Date.now())

        const savedAt = draft.updatedAt
        try {
            const result = await withTimeout(saveFn(draft.data), SYNC_TIMEOUT_MS)

            // Apollo's error link swallows network errors and returns null —
            // treat as a failed attempt so we retry instead of clearing the draft.
            if (result == null) {
                throw new Error("Save returned empty result (likely network error)")
            }

            // Check for conflict response
            if (result && typeof result === "object" && result.conflict) {
                const recovery = await recoverDraft({ key, draft, result, replayAgeMs })
                if (recovery === "matched") {
                    await clearLocalDraftIfNotNewer(key, savedAt)
                    DraftSyncSuccess.raise({ key })
                    return { status: "synced" }
                }
                if (recovery === "retry") continue

                await markConflict(key)
                DraftSyncConflict.raise({ key, result })
                console.warn("Draft sync preserved local draft after conflict", {
                    key,
                    editorType: draft.editorType,
                    replayAgeMs,
                })
                return { status: "conflict" }
            }

            // Stale write-order rejection — retry with a fresh sequence
            if (result && typeof result === "object" && result.stale) {
                console.warn("Draft sync replay hit stale write-order rejection", {
                    key,
                    editorType: draft.editorType,
                    attempt: attempt + 1,
                    replayAgeMs,
                })
                throw new Error("Save rejected as stale (out-of-order write)")
            }

            // Skipped (e.g. lock lost) or explicit server failure — retry
            if (result && typeof result === "object" && (result.skipped || result.success === false)) {
                const recovery = await recoverDraft({ key, draft, result, replayAgeMs })
                if (recovery === "matched") {
                    await clearLocalDraftIfNotNewer(key, savedAt)
                    DraftSyncSuccess.raise({ key })
                    return { status: "synced" }
                }
                if (recovery === "retry") continue
                throw new Error("Save was not persisted (skipped or success:false)")
            }

            // Success - only clear if no newer draft was written while in-flight
            await clearLocalDraftIfNotNewer(key, savedAt)
            DraftSyncSuccess.raise({ key })
            return { status: "synced" }
        } catch (e) {
            const isLockError = e?.message?.includes("Cannot lock draft for editing")
            if (isLockError) {
                // The draft is locked or published — no point retrying. Try recovery
                // to confirm server already has this data, then clear the orphan.
                const recovery = await recoverDraft({ key, draft, result: null, replayAgeMs })
                if (recovery === "matched") {
                    await clearLocalDraftIfNotNewer(key, savedAt)
                    DraftSyncSuccess.raise({ key })
                    return { status: "synced" }
                }
                // Even if recovery can't confirm a match, the draft is no longer
                // editable on the server. Clear it to stop the retry loop.
                console.warn("Draft sync clearing orphaned draft (server lock rejected)", {
                    key,
                    editorType: draft.editorType,
                    replayAgeMs,
                    originInstance: draft.originInstance,
                })
                await clearLocalDraft(key)
                return { status: "cleared" }
            }
            console.warn(`Draft sync attempt ${attempt + 1}/${MAX_RETRIES} failed for ${key}:`, e)
            if (attempt < MAX_RETRIES - 1) {
                await delay(RETRY_BACKOFF_MS * (attempt + 1))
            }
        }
    }
    // Retries exhausted — stamp cooldown so periodic sync backs off
    const finalDraft = await getLocalDraft(key)
    console.warn("Draft sync exhausted retries", {
        key,
        editorType: finalDraft?.editorType,
        replayAgeMs: finalDraft?.updatedAt ? Date.now() - finalDraft.updatedAt : null,
        hasConflict: finalDraft?.conflictDetected ?? false,
    })
    if (finalDraft) {
        await updateLocalDraftMetadata(key, { syncFailedAt: Date.now() })
    }
    return { status: "failed" }
}

/**
 * Clear all draft data and save function registrations.
 * Called on logout to prevent cross-user data leakage on shared browsers.
 */
export async function clearOnLogout() {
    for (const key of Object.keys(saveFunctions)) {
        delete saveFunctions[key]
    }
    await clearAllDrafts()
    clearEmergencyDrafts()
}

/**
 * Force-sync a specific draft after a conflict.
 * Uses the same draft payload but sets forceOverwrite for the downstream save path.
 * @param {string} key
 * @returns {Promise<boolean>} true when force-save succeeded
 */
export async function forceSyncDraft(key) {
    if (!key || syncingKeys.has(key)) return false

    const draft = await getLocalDraft(key)
    if (!draft) return false
    if (draft.data == null) return false

    const saveFn = resolveSaveFunction(draft)
    if (!saveFn) return false

    syncingKeys.add(key)
    try {
        const savedAt = draft.updatedAt
        const result = await withTimeout(saveFn(draft.data, { forceOverwrite: true }), SYNC_TIMEOUT_MS)
        if (result == null) return false
        if (result && typeof result === "object" && (result.conflict || result.stale)) return false
        if (result && typeof result === "object" && (result.skipped || result.success === false)) return false
        await clearLocalDraftIfNotNewer(key, savedAt)
        DraftSyncSuccess.raise({ key })
        return true
    } catch (e) {
        console.warn(`Force sync failed for ${key}:`, e)
        return false
    } finally {
        syncingKeys.delete(key)
    }
}

function clearEmergencyDrafts() {
    try {
        const prefix = `sfg20-emergency-draft:${currentUserPrefix()}`
        const keys = []
        for (let i = 0; i < localStorage.length; i++) {
            const k = localStorage.key(i)
            if (k?.startsWith(prefix)) keys.push(k)
        }
        for (const k of keys) localStorage.removeItem(k)
    } catch (e) {
        // localStorage may be unavailable
    }
}

const PERIODIC_SYNC_INTERVAL_MS = 30000

let started = false

/**
 * Start the draft sync manager. Listens for connectivity events and syncs pending drafts.
 */
export function startDraftSyncManager() {
    if (started) return
    started = true

    Connected.on(syncPendingDrafts)
    ServerOnline.on(syncPendingDrafts)
    UserLoggedOut.on(clearOnLogout)
    window.addEventListener("online", syncPendingDrafts)

    // Periodic safety net: retry syncing pending drafts every 30 seconds when the
    // browser reports it is online. This catches edge cases where event-based triggers
    // (SSE reconnect, browser online event) fail to fire or fire too early.
    setInterval(() => {
        if (navigator.onLine) syncPendingDrafts()
    }, PERIODIC_SYNC_INTERVAL_MS)
}

function resolveSaveFunction(draft) {
    // Prefer key-specific handler (editor currently mounted), fall back to
    // editorType handler which reconstructs the save from stored context.
    let saveFn = saveFunctions[draft.key]
    if (!saveFn) {
        const fallback = fallbackSaveFunctions[draft.editorType]
        if (fallback && draft.saveContext) {
            saveFn = (data, options) => fallback(data, draft.saveContext, options)
        }
    }
    return saveFn
}

async function recoverDraft({ key, draft, result, replayAgeMs }) {
    const recoveryFn = draftRecoveryFunctions[draft.editorType]
    if (!recoveryFn) return null

    try {
        const recovery = await recoveryFn({ key, draft, result, replayAgeMs })
        if (!recovery) return null

        if (recovery.status === "matched") {
            console.info("Draft sync recovery matched server state and cleared local draft", {
                key,
                editorType: draft.editorType,
                replayAgeMs,
            })
            return "matched"
        }

        if (recovery.status === "retry") {
            const currentDraft = await getLocalDraft(key)
            const currentSaveContext = currentDraft?.saveContext ?? draft.saveContext ?? {}
            const nextSaveContext = mergeRecoverySaveContext(
                currentSaveContext,
                draft.saveContext,
                recovery.saveContext ?? draft.saveContext
            )
            const nextServerVersion = recovery.serverVersion ?? draft.serverVersion
            await updateLocalDraftMetadata(key, {
                saveContext: nextSaveContext,
                serverVersion: nextServerVersion,
            })
            console.info("Draft sync recovery refreshed local version context and will retry", {
                key,
                editorType: draft.editorType,
                replayAgeMs,
                serverVersion: nextServerVersion,
            })
            return "retry"
        }

        if (recovery.status === "conflict") {
            console.warn("Draft sync recovery preserved local draft after conflict", {
                key,
                editorType: draft.editorType,
                replayAgeMs,
            })
            return null
        }
    } catch (e) {
        console.warn("Draft sync recovery failed", {
            key,
            editorType: draft.editorType,
            replayAgeMs,
            message: e?.message || String(e),
        })
    }

    return null
}

function delay(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms))
}

function mergeRecoverySaveContext(currentSaveContext = {}, previousSaveContext = {}, recoverySaveContext = {}) {
    const patch = {}
    for (const [key, value] of Object.entries(recoverySaveContext)) {
        if (value !== previousSaveContext?.[key]) {
            patch[key] = value
        }
    }
    return {
        ...currentSaveContext,
        ...patch,
    }
}
